import React from "react";
import { Box, Typography, Chip, IconButton } from "@mui/material";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CancelIcon from "@mui/icons-material/Cancel";
import FormikLabel from "@/components/forms/FormikLabel";

const FileUploadComponent = ({ field }) => {
  const { errors, touched, values, setFieldValue } = useFormikContext();

  const handleFileChange = (event) => {
    const files = event.currentTarget.files;
    const newFiles = Array.from(files);

    // Add single or multiple files to Formik state
    setFieldValue(
      field.fieldId,
      field.multiple
        ? [...(values[field.fieldId] || []), ...newFiles]
        : newFiles[0]
    );
  };

  const handleFileDelete = (index) => {
    if (field.multiple) {
      const updatedFiles = values[field.fieldId].filter((_, i) => i !== index);
      setFieldValue(field.fieldId, updatedFiles);
    } else {
      setFieldValue(field.fieldId, null);
    }
  };

  const truncateFileName = (name) => {
    if (typeof name !== "string") {
      return ""; // Return an empty string or handle it as needed
    }

    return name.length > 20 ? `${name.slice(0, 20)}...` : name;
  };

  return (
    <Box display="flex" flexDirection="column">
      <FormikLabel text={field.fieldName} required={field.required} />
      <Box display="flex" flexDirection="column" gap={1}>
        <Box
          display="flex"
          alignItems="center"
          border="1px solid #e0e0e0"
          borderRadius="4px"
        >
          <Box
            display="flex"
            gap={1}
            flexGrow={1}
            sx={{
              overflowX: "auto",
              maxWidth: "90%",
              overflowY: "hidden",
              maxHeight: "50px",
              scrollBehavior: "smooth",
              paddingRight: "5px",
              // Custom scrollbar styling
              "&::-webkit-scrollbar": {
                width: "6px", // Thin vertical scrollbar
                height: "6px", // Thin horizontal scrollbar
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1", // Light gray track
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888", // Dark gray thumb
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555", // Darker on hover
              },
            }}
          >
            {field.multiple && Array.isArray(values[field.fieldId]) ? (
              values[field.fieldId].length > 0 ? (
                values[field.fieldId].map((file, index) => (
                  <Chip
                    key={index}
                    label={truncateFileName(file.name)}
                    onDelete={() => handleFileDelete(index)}
                    color="primary"
                    size="small"
                  />
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ marginLeft: "8px" }}
                >
                  {field.placeholder}
                </Typography>
              )
            ) : values[field.fieldId] ? (
              <Chip
                label={truncateFileName(values[field.fieldId]?.name)}
                onDelete={() => handleFileDelete()}
                color="primary"
                size="small"
              />
            ) : (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ marginLeft: "8px" }}
              >
                {field.placeholder}
              </Typography>
            )}
          </Box>
          <IconButton component="label" color="primary">
            <FileUploadIcon />
            <input
              id={field.fieldId}
              name={field.fieldId}
              type="file"
              hidden
              multiple={field.multiple}
              onChange={handleFileChange}
            />
          </IconButton>
        </Box>
        {touched[field.fieldId] && errors[field.fieldId] && (
          <Typography color="error" variant="body2">
            {errors[field.fieldId]}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

FileUploadComponent.propTypes = {
  field: PropTypes.shape({
    fieldId: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    multiple: PropTypes.bool,
    placeholder: PropTypes.string,
  }).isRequired,
};

export default FileUploadComponent;
